import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('toggle', this.#onDetailsToggle, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
    this.#cleanupSubmenuObservers();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    const header = this.headerComponent;
    const runSync = () => {
      if (!header) return;
      /* Header-wide: split nav uses two <header-menu> roots; open <details> may not be under this instance. */
      for (const details of header.querySelectorAll('details.menu-list__disclosure[open]')) {
        if (details instanceof HTMLDetailsElement) this.#syncSubmenuToHeaderRow(details);
      }
      if (!header.hasAttribute('data-mega-open')) return;
      let submenu = header.querySelector('.menu-list__submenu[data-active]');
      if (!(submenu instanceof HTMLElement) && this.#state.activeItem) {
        submenu = findSubmenu(this.#state.activeItem);
      }
      if (submenu instanceof HTMLElement) {
        this.#syncMegaUnderlayFromSubmenu(submenu);
      }
    };
    runSync();
    requestAnimationFrame(runSync);
  }, 100);

  #overflowSubmenuListener = () => {
    for (const details of this.querySelectorAll('details.menu-list__disclosure[open]')) {
      if (!(details instanceof HTMLDetailsElement)) continue;
      this.#resetSubmenuPositioning(details);
      details.removeAttribute('open');
    }
    this.#deactivate();
  };

  /** @param {Event} event */
  #onDetailsToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!event.target.classList.contains('menu-list__disclosure')) return;
    if (!this.contains(event.target)) return;

    if (event.target.open) {
      for (const other of this.querySelectorAll('details.menu-list__disclosure[open]')) {
        if (other !== event.target && other instanceof HTMLDetailsElement) {
          this.#resetSubmenuPositioning(other);
          other.removeAttribute('open');
        }
      }
      this.dispatchEvent(new MegaMenuHoverEvent());
      this.#syncHeightFromDetails(event.target);
    } else {
      this.#resetSubmenuPositioning(event.target);
      const stillOpen = this.querySelector('details.menu-list__disclosure[open]');
      if (stillOpen instanceof HTMLDetailsElement) {
        this.#syncHeightFromDetails(stillOpen);
      } else {
        this.#clearLayout();
      }
    }
  };

  /**
   * Submenus are absolutely positioned inside narrow <details>; width:100% only spans the trigger.
   * Align the panel to .header__columns so megas span the header content area.
   * @param {HTMLDetailsElement} details
   */
  #syncSubmenuToHeaderRow(details) {
    const submenu = details.querySelector('[ref="submenu[]"]');
    if (!(submenu instanceof HTMLElement) || !this.headerComponent) return;

    const trigger = details.querySelector('[ref="menuitem"]');
    if (trigger?.closest('[slot="overflow"]')) {
      this.#resetSubmenuPositioning(details);
      return;
    }

    const alignTarget =
      this.headerComponent.querySelector('.header__columns') ??
      this.headerComponent.querySelector('.header__row--top') ??
      this.headerComponent;

    const aRect = alignTarget.getBoundingClientRect();
    const dRect = details.getBoundingClientRect();

    submenu.style.left = `${aRect.left - dRect.left}px`;
    submenu.style.width = `${aRect.width}px`;
  }

  /** @param {HTMLDetailsElement} details */
  #resetSubmenuPositioning(details) {
    const submenu = details.querySelector('[ref="submenu[]"]');
    if (submenu instanceof HTMLElement) {
      submenu.style.removeProperty('left');
      submenu.style.removeProperty('width');
    }
  }

  /** @param {HTMLDetailsElement} details */
  #syncHeightFromDetails = (details) => {
    const submenu = details.querySelector('[ref="submenu[]"]');
    if (!(submenu instanceof HTMLElement) || !this.headerComponent) return;

    // Same moment as native [open] on <details>: skip underlay height transition so the open
    // underlay doesn’t animate in behind an already-open summary.
    this.headerComponent.setAttribute('data-mega-open', '');

    submenu.dataset.active = '';
    this.#cleanupSubmenuObservers();

    const measure = () => {
      this.#syncSubmenuToHeaderRow(details);
      this.#syncMegaUnderlayFromSubmenu(submenu);
      const trigger = details.querySelector('[ref="menuitem"]');
      const isInOverflow = trigger?.closest('[slot="overflow"]') != null;
      let finalHeight = submenu.offsetHeight || 0;

      if (isInOverflow) {
        const overflowListHeight = this.#getOverflowListLinksHeight();
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      }

      this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
      this.dataset.overflowExpanded = isInOverflow.toString();
      this.ariaExpanded = 'true';
    };

    this.#submenuResizeObserver = new ResizeObserver(() => measure());
    this.#submenuResizeObserver.observe(submenu);

    measure();
    requestAnimationFrame(measure);
  };

  #clearLayout = () => {
    this.headerComponent?.removeAttribute('data-mega-open');
    this.#cleanupSubmenuObservers();
    this.#clearMegaUnderlayGeometry();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    for (const el of this.querySelectorAll('.menu-list__submenu[data-active]')) {
      if (el instanceof HTMLElement) delete el.dataset.active;
    }
    for (const details of this.querySelectorAll('details.menu-list__disclosure')) {
      if (details instanceof HTMLDetailsElement) this.#resetSubmenuPositioning(details);
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    const overflowList = /** @type {HTMLElement} */ (this.refs.overflowMenu);
    return /** @type {HTMLElement | null} */ (overflowList.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    const overflowList = /** @type {HTMLElement} */ (this.refs.overflowMenu);
    return overflowList.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupSubmenuObservers();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupSubmenuObservers();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupSubmenuObservers();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    if (finalHeight > 0 && submenu instanceof HTMLElement) {
      this.#syncMegaUnderlayFromSubmenu(submenu);
    } else {
      this.#clearMegaUnderlayGeometry();
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    if (finalHeight > 0) {
      this.headerComponent.setAttribute('data-mega-open', '');
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.removeAttribute('data-mega-open');
    this.#clearMegaUnderlayGeometry();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Sizes the open header underlay to the mega panel (`.menu-list__submenu-inner`) so the gradient
   * and border match the menu, not the full header-component width.
   * @param {HTMLElement} submenu
   */
  #syncMegaUnderlayFromSubmenu(submenu) {
    const header = this.headerComponent;
    if (!header) return;

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (!(inner instanceof HTMLElement)) {
      this.#clearMegaUnderlayGeometry();
      return;
    }

    const hr = header.getBoundingClientRect();
    const ir = inner.getBoundingClientRect();
    const isRtl = getComputedStyle(header).direction === 'rtl';
    const insetStart = isRtl ? Math.round(hr.right - ir.right) : Math.round(ir.left - hr.left);

    header.style.setProperty('--mega-underlay-inset-inline-start', `${insetStart}px`);
    header.style.setProperty('--mega-underlay-width', `${Math.round(ir.width)}px`);
  }

  #clearMegaUnderlayGeometry() {
    this.headerComponent?.style.removeProperty('--mega-underlay-inset-inline-start');
    this.headerComponent?.style.removeProperty('--mega-underlay-width');
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuObservers() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
